<template>
  <div class="content-wrapper view-aptlist-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <!-- Header -->
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center gap-2">
              <b-button variant="outline" size="sm" @click="handleBack" v-b-tooltip.hover title="ย้อนกลับ">
                <i class="fas fa-chevron-left" style="font-size: 20px"></i>
              </b-button>
              <h4 class="mb-0">การจัดการสต๊อก</h4>
              <AdjustmentStatusBadge v-if="stockAdjustment" :status="stockAdjustment.normalizedStatus" />
            </div>
            <div v-if="
              stockAdjustment &&
              stockAdjustment.normalizedStatus ===
              ADJUSTMENT_STATUS.WAITING_HQ
            " class="d-flex gap-4">
              <b-button variant="outline-danger" size="sm" class="btn-delete-adj" @click="handleDelete">
                ยกเลิกรายการ
              </b-button>
              <b-button
                v-if="stockAdjustment.rawType === STOCK_TYPE_NAME.PHYSICAL_COUNT"
                variant="outline-primary"
                size="sm"
                class="btn-compare-phy"
                @click="triggerCompareFileInput"
              >
                เปรียบเทียบสต๊อกจากไฟล์
              </b-button>
              <input
                ref="compareFileInput"
                type="file"
                accept=".xlsx,.xls"
                style="display: none"
                @change="handleCompareFile"
              />
              <b-button variant="danger" size="sm" class="btn-reject-adj" @click="$refs.actionModal.showReject()">
                ปฏิเสธ
              </b-button>
              <b-button variant="primary" size="sm" class="btn-approve-adj" @click="$refs.actionModal.showApprove()">
                อนุมัติ
              </b-button>
            </div>
          </div>

          <!-- Two-column layout -->
          <div v-if="stockAdjustment" class="stock-management-detail-layout">
            <!-- Left: metadata -->
            <div class="stock-management-detail-info">
              <div class="info-row">
                <span class="info-label">รหัส:</span>
                <span class="info-value">{{
                  stockAdjustment.adjustmentNo || "-"
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ประเภทการจัดการ:</span>
                <span class="info-value">{{
                  stockAdjustment.type || "-"
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">เหตุผล:</span>
                <span class="info-value">{{
                  stockAdjustment.reason || "-"
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ชื่อผู้ทำรายการ:</span>
                <span class="info-value">{{
                  stockAdjustment.createdByName || "-"
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ต้องได้รับการอนุมัติจาก:</span>
                <span class="info-value">
                  <ApprovalSteps :show-bm="true" :show-hq="true" />
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">วันที่มีผล:</span>
                <span class="info-value">{{
                  stockAdjustment.transactionDate
                    ? formatDate(stockAdjustment.transactionDate)
                    : "-"
                }}</span>
              </div>
              <div v-if="
                stockAdjustment.rawType === STOCK_TYPE_NAME.PHYSICAL_COUNT &&
                stockAdjustment.countedDt
              " class="info-row">
                <span class="info-label">วันที่นับจริง:</span>
                <span class="info-value">{{
                  formatDate(stockAdjustment.countedDt)
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">วันที่ทำรายการ:</span>
                <span class="info-value">{{
                  stockAdjustment.createdDate
                    ? formatDate(stockAdjustment.createdDate)
                    : "-"
                }}</span>
              </div>
              <div v-if="showRejectReason" class="reject-reason-box">
                <div class="reject-reason-title">เหตุผลที่ถูกปฏิเสธ:</div>
                <div>{{ stockAdjustment.rejectReason }}</div>
              </div>
            </div>

            <!-- Right: product table -->
            <div class="stock-management-detail-table">
              <b-table id="hqApprovalDetailTable" hover small responsive head-variant="light"
                :items="stockAdjustment.products || []" :fields="productFields">
                <template #head(totalQty)>
                  <div class="text-right">
                    <div>จำนวนสต๊อก</div>
                    <div v-if="stockAdjustment?.transactionDate">
                      {{ formatDate(stockAdjustment.transactionDate) }}
                    </div>
                  </div>
                </template>
                <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>
                <template v-slot:cell(image)="row">
                  <ProductImage :src="row.item.imageUrl || null" />
                </template>
                <template v-slot:cell(productNo)="row">
                  {{ row.item.no || row.item.productNo || "-" }}
                </template>
                <template v-slot:cell(productName)="row">
                  {{ row.item.description || "-" }}
                </template>
                <template v-slot:cell(adjustmentQty)="row">
                  {{ formatAdjustQty(row.item.adjustmentQty) }}
                </template>
                <template v-slot:cell(totalQty)="row">
                  {{ formatTotalQty(row.item.totalQty) }}
                </template>
                <template v-slot:cell(uom)="row">
                  {{
                    (row.item.uom && row.item.uom.description) ||
                    row.item.uomName ||
                    "-"
                  }}
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>

      <HQApprovalActionModal ref="actionModal" :show-older-pending-warning="stockAdjustment && hasOlderPendingAdjustments
        " :is-older-physical-count-pending="hasOlderPhysicalCountPending" @approve="confirmApprove"
        @reject="confirmReject" />

      <CompareStockFromFileModal
        ref="compareModal"
        :items="compareItems"
      />

      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading"></Loading>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog.vue";
import HQApprovalActionModal from "@/components/ProductAndStock/HQApprovalActionModal.vue";
import ProductImage from "@/components/table/stock/component/ProductImage.vue";
import AdjustmentStatusBadge from "@/components/badge/ProductAndStock/AdjustmentStatusBadge.vue";
import ApprovalSteps from "@/components/badge/ProductAndStock/ApprovalSteps.vue";
import CompareStockFromFileModal from "@/components/modal/ProductAndStock/CompareStockFromFileModal.vue";
import { ADJUSTMENT_STATUS, STOCK_TYPE_NAME } from "@/constants/productStock";
import { mapActions, mapMutations, mapGetters } from "vuex";
import { formatDate } from "@/utils/utils";

export default {
  name: "HQApprovalDetail",
  components: {
    Dialog,
    Loading,
    ProductImage,
    AdjustmentStatusBadge,
    ApprovalSteps,
    HQApprovalActionModal,
    CompareStockFromFileModal,
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fromRoute = from;
    });
  },
  data() {
    return {
      ADJUSTMENT_STATUS,
      STOCK_TYPE_NAME,
      isLoading: false,
      stockAdjustment: null,
      fromRoute: null,
      compareItems: [],
    };
  },
  async mounted() {
    await this.loadData();
  },
  computed: {
    ...mapGetters({
      getHQStockAdjustmentList:
        "moduleProductInventory/getHQStockAdjustmentList",
      productList: "moduleProductInventory/getProductList",
    }),
    hasOlderPhysicalCountPending() {
      if (!this.stockAdjustment?.transactionDate) return false;
      const branchUrl = this.$route.params.branch;
      return (this.getHQStockAdjustmentList || []).some(item => {
        if (String(item.id) === String(this.stockAdjustment.id)) return false;
        if (item.status !== ADJUSTMENT_STATUS.WAITING_HQ) return false;
        if (item.type !== STOCK_TYPE_NAME.PHYSICAL_COUNT) return false;
        if (item.branchUrl !== branchUrl) return false;
        return item.transactionDt < this.stockAdjustment.transactionDate;
      });
    },
    hasOlderNonPhysicalCountPending() {
      if (!this.stockAdjustment?.transactionDate) return false;
      const branchUrl = this.$route.params.branch;
      return (this.getHQStockAdjustmentList || []).some(item => {
        if (String(item.id) === String(this.stockAdjustment.id)) return false;
        if (item.status !== ADJUSTMENT_STATUS.WAITING_HQ) return false;
        if (item.type === STOCK_TYPE_NAME.PHYSICAL_COUNT) return false;
        if (item.branchUrl !== branchUrl) return false;
        return item.transactionDt < this.stockAdjustment.transactionDate;
      });
    },
    hasOlderPendingAdjustments() {
      return (
        this.hasOlderNonPhysicalCountPending ||
        this.hasOlderPhysicalCountPending
      );
    },
    showRejectReason() {
      return (
        this.stockAdjustment?.normalizedStatus === ADJUSTMENT_STATUS.REJECTED &&
        !!this.stockAdjustment?.rejectReason
      );
    },
    needsHqApprove() {
      if (!this.stockAdjustment) return false;
      const { rawType, transactionDate, createdDate } = this.stockAdjustment;
      if (rawType === STOCK_TYPE_NAME.GENERAL || rawType === STOCK_TYPE_NAME.PHYSICAL_COUNT) return true;
      const tDate = transactionDate
        ? new Date(transactionDate).toDateString()
        : null;
      const cDate = createdDate ? new Date(createdDate).toDateString() : null;
      return tDate !== cDate;
    },
    productFields() {
      const fields = [
        { key: "index", label: "", thStyle: { width: "40px" } },
        {
          key: "image",
          label: "",
          thStyle: { width: "50px", minWidth: "50px" },
        },
        { key: "productNo", label: "รหัส", thStyle: { minWidth: "100px" } },
        {
          key: "productName",
          label: "ชื่อสินค้า",
          thStyle: { minWidth: "160px" },
        },
        {
          key: "adjustmentQty",
          label: "จำนวนปรับปรุง",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        },
      ];
      if (this.stockAdjustment?.rawType === STOCK_TYPE_NAME.PHYSICAL_COUNT) {
        fields.push({
          key: "totalQty",
          label: "จำนวนสต๊อก",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        });
      }
      fields.push({
        key: "uom",
        label: "หน่วยสินค้า",
        thStyle: { minWidth: "100px" },
      });
      return fields;
    },
  },
  methods: {
    ...mapActions({
      fetchStockAdjustmentById:
        "moduleProductInventory/fetchStockAdjustmentById",
      hqApproveStockAdjustmentAction:
        "moduleProductInventory/hqApproveStockAdjustment",
      deleteStockAdjustmentAction:
        "moduleProductInventory/deleteStockAdjustment",
      fetchHQStockAdjustmentList:
        "moduleProductInventory/fetchHQStockAdjustmentList",
      fetchProductList: "moduleProductInventory/fetchProductList",
    }),
    ...mapMutations({
      patchHQStockAdjustmentStatus:
        "moduleProductInventory/patchHQStockAdjustmentStatus",
      removeHQStockAdjustment: "moduleProductInventory/removeHQStockAdjustment",
    }),
    async loadData() {
      this.isLoading = true;
      try {
        const { clinicUrl, branch, adjustmentId } = this.$route.params;
        if (!this.productList.length) {
          await this.fetchProductList({ clinicUrl, branchUrl: branch });
        }
        const res = await this.fetchStockAdjustmentById({
          clinicUrl,
          branchUrl: branch,
          adjustmentId,
        });
        if (!res) {
          this.$refs.Dialog.showToast("danger", "รายการนี้ถูกลบไปแล้ว");
          setTimeout(() => this.goBack(), 1500);
          return;
        }
        const createdByName = [
          res.createdBy?.firstNameTh,
          res.createdBy?.lastNameTh,
        ]
          .filter(Boolean)
          .join(" ");
        this.stockAdjustment = {
          id: res.id,
          adjustmentNo: res.adjustmentNo,
          type: this.getAdjustmentDetail(res.type),
          rawType: res.type,
          reason: res.reason,
          status: res.status,
          normalizedStatus: this.normalizeAdjStatus(res.status),
          syncDt: res.syncDt,
          createdByName: createdByName || "-",
          createdDate: res.createdDate,
          transactionDate: res.transactionDate,
          countedDt: res.countedDt,
          rejectReason: res.rejectReason || null,
          products: (res.stockLedgers || []).map(item => ({
            productId: item.productId,
            no: item.product?.no || "-",
            description: item.product?.description || "-",
            imageUrl: this.productList.find(p => p.productId === item.productId)?.imageUrl || null,
            adjustmentQty: item.qtyUom,
            totalQty: item.totalQty,
            uom: item.uom || null,
            uomName: item.uom?.description || "-",
          })),
        };
      } catch (err) {
        this.$refs.Dialog.showToast("danger", "โหลดข้อมูลไม่สำเร็จ");
        this.goBack();
      } finally {
        this.isLoading = false;
      }
    },
    normalizeAdjStatus(status) {
      const remap = { PENDING: "WAITING_BM", CONFIRMED: "COMPLETED" };
      return remap[ status ] || status || "";
    },
    getAdjustmentDetail(docType) {
      switch (docType) {
        case STOCK_TYPE_NAME.GENERAL:
          return "ปรับปรุงทั่วไป";
        case STOCK_TYPE_NAME.RETURN:
          return "ส่งคืนสินค้า";
        case STOCK_TYPE_NAME.PHYSICAL_COUNT:
          return "ตรวจนับสต๊อก";
        case STOCK_TYPE_NAME.WITHDRAW:
          return "ตัดเบิกของ";
        default:
          return docType || "-";
      }
    },
    async confirmApprove() {
      this.isLoading = true;
      try {
        const { clinicUrl, branch, adjustmentId } = this.$route.params;
        const res = await this.hqApproveStockAdjustmentAction({
          clinicUrl,
          branchUrl: branch,
          adjustmentId,
          action: "APPROVE",
        });
        this.$refs.Dialog.showToast("success", "อนุมัติการจัดการสต๊อกสำเร็จ");
        const updatedStatus = res?.data?.data?.status;
        if (updatedStatus) {
          this.stockAdjustment = {
            ...this.stockAdjustment,
            status: updatedStatus,
            normalizedStatus: this.normalizeAdjStatus(updatedStatus),
          };
          this.patchHQStockAdjustmentStatus({
            id: Number(adjustmentId),
            status: updatedStatus,
          });
        } else {
          await this.fetchHQStockAdjustmentList({ clinicUrl });
          await this.loadData();
        }
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage;
        this.$refs.Dialog.showToast(
          "danger",
          typeof errorMessage === "string"
            ? errorMessage
            : "อนุมัติการจัดการสต๊อกไม่สำเร็จ กรุณาลองใหม่"
        );
      } finally {
        this.isLoading = false;
      }
    },
    async confirmReject(rejectReason) {
      this.isLoading = true;
      try {
        const { clinicUrl, branch, adjustmentId } = this.$route.params;
        const res = await this.hqApproveStockAdjustmentAction({
          clinicUrl,
          branchUrl: branch,
          adjustmentId,
          action: "REJECT",
          rejectReason: rejectReason || undefined,
        });
        this.$refs.Dialog.showToast("success", "ปฏิเสธการจัดการสต๊อกสำเร็จ");
        const updatedStatus = res?.data?.data?.status;
        if (updatedStatus) {
          this.stockAdjustment = {
            ...this.stockAdjustment,
            status: updatedStatus,
            normalizedStatus: this.normalizeAdjStatus(updatedStatus),
            rejectReason: rejectReason || null,
          };
          this.patchHQStockAdjustmentStatus({
            id: Number(adjustmentId),
            status: updatedStatus,
          });
        } else {
          await this.fetchHQStockAdjustmentList({ clinicUrl });
          await this.loadData();
        }
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage;
        this.$refs.Dialog.showToast(
          "danger",
          typeof errorMessage === "string"
            ? errorMessage
            : "ปฏิเสธการจัดการสต๊อกไม่สำเร็จ กรุณาลองใหม่"
        );
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(value) {
      return formatDate(value);
    },
    formatAdjustQty(value) {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      const formatted = num.toLocaleString("en-US");
      return num > 0 ? `+${formatted}` : formatted;
    },
    formatTotalQty(value) {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      return num.toLocaleString("en-US");
    },
    async handleDelete() {
      const result = await this.$refs.Dialog.showAlertConfirm(
        "ยกเลิกการจัดการสต๊อก ?",
        "คุณต้องการยกเลิกการจัดการสต๊อกนี้ใช่หรือไม่"
      );
      if (!result.value) return;
      this.isLoading = true;
      try {
        const { clinicUrl, branch, adjustmentId } = this.$route.params;
        const res = await this.deleteStockAdjustmentAction({
          clinicUrl,
          branchUrl: branch,
          adjustmentId,
        });
        this.$refs.Dialog.showToast("success", "ยกเลิกการจัดการสต๊อกสำเร็จ");
        const updatedStatus = res?.data?.data?.status;
        if (updatedStatus) {
          this.stockAdjustment = {
            ...this.stockAdjustment,
            status: updatedStatus,
            normalizedStatus: this.normalizeAdjStatus(updatedStatus),
          };
          this.patchHQStockAdjustmentStatus({
            id: Number(adjustmentId),
            status: updatedStatus,
          });
        } else {
          this.removeHQStockAdjustment({ id: Number(adjustmentId) });
          setTimeout(() => this.goBack(), 1000);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage;
        this.$refs.Dialog.showToast(
          "danger",
          typeof errorMessage === "string"
            ? errorMessage
            : "ยกเลิกการจัดการสต๊อกไม่สำเร็จ กรุณาลองใหม่"
        );
      } finally {
        this.isLoading = false;
      }
    },
    triggerCompareFileInput() {
      this.$refs.compareFileInput.click();
    },
    async handleCompareFile(event) {
      const file = event.target.files[0];
      this.$refs.compareFileInput.value = "";
      if (!file) return;

      try {
        const XLSX = await import("xlsx");
        const buffer = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = e => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsArrayBuffer(file);
        });

        const workbook = XLSX.read(buffer, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawRows = XLSX.utils.sheet_to_json(worksheet);

        const requiredCols = ["Product ID", "จำนวนสต๊อกหลังปรับปรุง"];
        const isValid = rawRows?.length && requiredCols.every(col => col in rawRows[0]);
        if (!isValid) {
          this.$refs.Dialog.showToast("danger", "รูปแบบไฟล์ไม่ถูกต้อง กรุณาตรวจสอบและนำเข้าใหม่อีกครั้ง");
          return;
        }

        const fileMap = {};
        rawRows.forEach(row => {
          const code = String(row["รหัสสินค้า"] || "").trim();
          if (code) fileMap[code] = row["จำนวนสต๊อกหลังปรับปรุง"];
        });

        const products = this.stockAdjustment?.products || [];
        const systemCodes = new Set(products.map(p => String(p.no || p.productNo || "").trim()));

        const mapped = products.map(p => {
          const key = String(p.no || p.productNo || "").trim();
          const inFile = key !== "" && key in fileMap;
          const rawFileQty = inFile ? fileMap[key] : null;
          let type;
          if (!inFile) {
            type = "not_in_file";
          } else if (Number(rawFileQty) !== Number(p.totalQty)) {
            type = "mismatch";
          } else {
            type = "match";
          }
          return {
            imageUrl: p.imageUrl || null,
            productNo: p.no || p.productNo || "-",
            productName: p.description || "-",
            adjustmentQty: p.adjustmentQty,
            totalQty: p.totalQty,
            fileQty: inFile ? rawFileQty : "ไม่มีสินค้าในไฟล์",
            uomName: (p.uom && p.uom.description) || p.uomName || "-",
            _type: type,
          };
        });

        const extraRows = rawRows
          .filter(row => {
            const code = String(row["รหัสสินค้า"] || "").trim();
            return code && !systemCodes.has(code);
          })
          .map(row => ({
            imageUrl: null,
            productNo: row["รหัสสินค้า"] || "-",
            productName: row["ชื่อสินค้า"] || "-",
            adjustmentQty: null,
            totalQty: "ไม่มีสินค้าในระบบ",
            fileQty: row["จำนวนสต๊อกหลังปรับปรุง"],
            uomName: row["หน่วยสินค้า"] || "-",
            _type: "not_in_system",
          }));

        const typeOrder = { not_in_system: 0, not_in_file: 1, mismatch: 2, match: 3 };
        this.compareItems = [...extraRows, ...mapped].sort(
          (a, b) => typeOrder[a._type] - typeOrder[b._type]
        );

        this.$refs.compareModal.show();
      } catch (err) {
        console.error("Compare file error:", err);
        this.$refs.Dialog.showToast("danger", "อ่านไฟล์ไม่สำเร็จ กรุณาตรวจสอบรูปแบบไฟล์");
      }
    },
    handleBack() {
      if (this.fromRoute?.name) {
        this.$router.go(-1);
      } else {
        this.goBack();
      }
    },
    goBack() {
      this.$router.push({
        name: "HQApproval",
        params: { clinicUrl: this.$route.params.clinicUrl },
      });
    },
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}

.stock-management-detail-layout {
  display: flex;
  gap: 24px;
  border: 1px solid #dee2e6;
  padding: 20px;
}

.stock-management-detail-info {
  flex: 0 0 30%;
  min-width: 0;
  border-right: 1px solid #dee2e6;
  padding-right: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stock-management-detail-table {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
}

.info-row {
  line-height: 1.6;
  display: flex;
}

.info-label {
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  flex-shrink: 0;
}

.info-value {
  margin-left: 4px;
  word-break: break-word;
  min-width: 0;
  flex: 1;
}

.btn-approve-adj {
  padding: 8px 50px;
  font-size: 14px;
  font-weight: 600;
}

.reject-reason-box {
  background: #ffd9dd;
  border-radius: 4px;
  padding: 16px;
}

.reject-reason-title {
  font-weight: 600;
  font-size: 16px;
  color: #dd3333;
  margin-bottom: 4px;
}

.btn-delete-adj {
  padding: 8px 30px;
  font-size: 14px;
  font-weight: 600;
  border-color: #dd3333;
  color: #dd3333;
}

.btn-compare-phy {
  padding: 8px 30px;
  font-size: 14px;
  font-weight: 600;
  border-color: #5299D6;
  color: #5299D6;
  &:hover {
    background-color: #5299D6;
    color: #fff;  
  }
}

.btn-delete-adj:hover {
  background-color: #dd3333;
  color: #fff;
}

::v-deep #hqApprovalDetailTable thead th,
::v-deep #hqApprovalDetailTable tbody td {
  vertical-align: middle !important;
}

.btn-reject-adj {
  padding: 8px 50px;
  font-size: 14px;
  font-weight: 600;
  background-color: #dd3333;
  border-color: #dd3333;
  color: #fff;
}

.btn-reject-adj:hover {
  background-color: #bb2222;
  border-color: #bb2222;
  color: #fff;
}

@media (max-width: 768px) {
  .stock-management-detail-layout {
    flex-direction: column;
  }

  .stock-management-detail-info {
    flex: none;
    border-right: none;
    padding-right: 0;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 16px;
  }
}
</style>
